import type { ToothFullHeadMPRSlice } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import type { FullHeadMPRViewSnapshot } from '@/shared/graphics/medicalImageRender/MedicalImageRender';

import {
  isSameFullHeadMprAnnotationPlane,
  makeFullHeadMprEditorStateRequest,
  makeSavedFullHeadMprView,
  resolveFullHeadMprSeriesContext,
  type FullHeadMprOriginalSlice,
} from '@/features/MPR';

import type { FullHeadMprEditorTarget } from '../config';

export const makeFullHeadMprEditorTarget = ({
  original,
  previous,
  slices,
  snapshot,
  toothID,
}: {
  slices: ToothFullHeadMPRSlice[];
  snapshot: FullHeadMPRViewSnapshot;
  toothID: string;
  original?: FullHeadMprOriginalSlice | null;
  previous?: FullHeadMprEditorTarget;
}): FullHeadMprEditorTarget | null => {
  const previousMatches =
    previous &&
    (!previous.recordID ||
      slices.some((slice) => slice.ID === previous.recordID)) &&
    isSameFullHeadMprAnnotationPlane({
      isSeries: previous.isSeries,
      left: previous.original.view,
      right: snapshot.view,
    });
  const request = makeFullHeadMprEditorStateRequest({
    original: previousMatches ? previous.original : original,
    slices,
    snapshot,
    toothID,
  });
  const recordID =
    request.Target.case === 'SliceID' ? request.Target.value : undefined;
  const record = slices.find((slice) => slice.ID === recordID);
  const seriesContext = resolveFullHeadMprSeriesContext({
    slice: record,
    target: request.Target,
  });
  const view = request.EditorState?.AnnotationView
    ? makeSavedFullHeadMprView(
        request.EditorState.AnnotationView,
        seriesContext,
      )
    : null;
  if (!view || !request.Geometry) {
    return null;
  }

  return {
    id:
      recordID ??
      (previousMatches
        ? previous.id
        : `${toothID}:${JSON.stringify(request.Target)}`),
    isSeries:
      request.Target.case === 'Series' ||
      Boolean(record && record.Source.case !== 'View'),
    original: {
      geometry: request.Geometry,
      target: request.Target,
      view,
    },
    recordID,
  };
};
